import Amplify from 'aws-amplify'

import { AMPLIFY_AUTH_FLOW_TYPE, AMPLIFY_REGION } from '../../../config'
import { getThriveAuthConfig } from '@api/platform'

/**
 * singleton to handle dynamically configuring Amplify.
 *
 * Amplify will throw errors when attempting to use Amplify's methods before a
 * configuration is set. it's possible this can happen because auth state depends
 * on calling Amplify's user-auth related methods on app load.
 *
 * abstracting out the configuration to protect Amplify-related methods from being
 * called before the configuration is set.
 */

type AmplifyConfigurationType = {
  /**
   * getter for client to consume and know whether the configuration is implemented.
   * "client" is a amplify-related method.
   */
  isConfigured: boolean
  /**
   * used by methods that will kick off a configuration. right now, these are
   * user-related form submission methods.
   */
  configure(userPoolId: string, userPoolWebClientId: string): void
}

export type AmplifyConfigurationPayloadType = {
  cognitoUserPoolClientId: string
  cognitoUserPoolId: string
  emailDomains: string[]
  oktaConfiguration?: {
    idp: string
    clientId: string
    metadataUrl: string
  }
}

class AmplifyConfiguration implements AmplifyConfigurationType {
  private _isConfigured: boolean

  get isConfigured() {
    // Cypress needs to skip this check in the integration tests
    if (window.Cypress) {
      return true
    }

    return this._isConfigured
  }

  constructor() {
    this._isConfigured = false
  }

  configure(userPoolId: string, userPoolWebClientId: string) {
    const options = {
      userPoolId,
      userPoolWebClientId,
      authenticationFlowType: AMPLIFY_AUTH_FLOW_TYPE,
      region: AMPLIFY_REGION,
    }

    Amplify.configure({ Auth: options })
    this._isConfigured = true
  }

  // used by sign-in, forgot-your-password email form
  async configureViaEmail(
    email: string
  ): Promise<AmplifyConfigurationPayloadType> {
    const thriveAuthConfig = await getThriveAuthConfig({ email })
    // after collecting the required the Cognito Ids from Thrive platform,
    // configure the Amplify library
    const { cognitoUserPoolId, cognitoUserPoolClientId } = thriveAuthConfig
    this.configure(cognitoUserPoolId, cognitoUserPoolClientId)
    return thriveAuthConfig
  }
}

const amplifyConfiguration = new AmplifyConfiguration()
export { amplifyConfiguration }
